/**
 * Accounting Form Handler - Matching Course Registration UI
 *
 * Handles form interactions, conditional logic, and price calculation
 *
 * @package KTVHVN_Course_Registration
 * @since 1.1.0
 */

(function ($) {
    'use strict';

    // Exit if not accounting mode
    if (!window.ktvhvnAccounting) {
        return;
    }

    window.KTVHVN = window.KTVHVN || {};

    /**
     * Accounting Form Manager
     */
    window.KTVHVN.AccountingForm = class {

        constructor() {
            this.config = window.ktvhvnAccounting.config;

            // DEBUG: Log received config
            console.log('[DEBUG AccountingForm] Received config:', this.config);
            console.log('[DEBUG AccountingForm] Services:', this.config.services);
            if (this.config.services && this.config.services.length > 0) {
                console.log('[DEBUG AccountingForm] First service pricing_logic:', this.config.services[0].pricing_logic);
            }

            this.conditionalEngine = new window.KTVHVN.ConditionalEngine();
            this.pricingEngine = new window.KTVHVN.AccountingPricing(this.config);

            // Form IDs in template
            this.$serviceSelect = $('#service-select');
            this.$selectedService = $('#ktvhvn-selected-service');
            this.$addonSection = $('#ktvhvn-addon-services-section');
            this.$emptyState = $('#ktvhvn-empty-state');
            this.$paymentSection = $('#ktvhvn-payment-summary-section');
            this.$customerSection = $('#ktvhvn-customer-section');
            this.$priceSummary = $('#price-summary');
            this.$voucherRow = $('#ktvhvn-voucher-row');
            this.$discountRow = $('#ktvhvn-discount-row');
            this.$discountAmount = $('#ktvhvn-discount-amount');
            this.$discountCampaignName = $('#ktvhvn-discount-campaign-name');
            this.$subtotalAmount = $('#ktvhvn-subtotal-amount');
            this.$totalAmount = $('#ktvhvn-total-amount-top');

            this.state = {
                service: null,
                industry: '',
                months: 3,
                invoices: 0,
                revenue_bracket: 'under_2',
                products: 0,
                addons: [],
                discount: 0,
                discountName: '',
                selectedCampaignId: null,
                lastTotal: 0,
                isPrintingPdf: false // Flag to prevent duplicate PDF generation
            };

            this.init();
        }

        /**
         * Initialize form
         */
        init() {
            console.log('Accounting form initialized');

            // Bind events
            this.bindEvents();

            // Show empty state initially
            this.showEmptyState();
        }

        /**
         * Bind form events
         */
        bindEvents() {
            const self = this;

            // Service selection
            this.$serviceSelect.on('change select2:select', function () {
                const serviceId = $(this).val();
                if (serviceId) {
                    self.onServiceSelect(serviceId);
                } else {
                    self.removeService();
                }
            });

            // Form submit (registration button inside form)
            $('#accounting-service-form').on('submit', function (e) {
                e.preventDefault();
                self.handleSubmit('registration');
            });

            $('#submit-btn').on('click', function (e) {
                e.preventDefault();
                self.handleSubmit('registration');
            });

            // Empty state CTA
            $('#ktvhvn-empty-cta').on('click', function () {
                $('html, body').animate({
                    scrollTop: $('#ktvhvn-service-picker').offset().top - 100
                }, 500);
                self.$serviceSelect.focus();
            });

            // Remove service button (will be created dynamically)
            $(document).on('click', '#remove-service-btn', function () {
                self.removeService();
            });

            // Config field changes (will be created dynamically)
            // Business info fields
            $(document).on('change input select2:select', '#industry-select', function () {
                self.state.industry = $(this).val();
                self.updateConditionalAddons();
                self.calculatePrice();
            });

            $(document).on('change input', '#months-needed', function () {
                self.state.months = parseInt($(this).val(), 10) || 3;
                self.calculatePrice();
            });

            $(document).on('change input', '#invoices-quarter', function () {
                self.state.invoices = parseInt($(this).val(), 10) || 0;
                self.calculatePrice();
            });

            $(document).on('change', '#revenue-bracket', function () {
                self.state.revenue_bracket = $(this).val();
                self.calculatePrice();
            });

            $(document).on('change input', '#product-count', function () {
                self.state.products = parseInt($(this).val(), 10) || 0;
                self.calculatePrice();
            });

            // Addon checkboxes
            $(document).on('change', '.addon-checkbox-card input[type="checkbox"]', function () {
                self.onAddonChange();
            });

            // Submit buttons
            $('#ktvhvn-submit-registration-top').on('click', function () {
                self.handleSubmit('registration');
            });

            $('#ktvhvn-submit-payment-top, #ktvhvn-accounting-pay-btn').on('click', function () {
                self.handleSubmit('payment');
            });

            // Use namespace and .off() to prevent duplicate event binding
            $('#ktvhvn-print-quote-btn, #ktvhvn-accounting-print-btn')
                .off('click.printQuote')
                .on('click.printQuote', function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    // Prevent double-click by checking if button is disabled
                    if ($(this).prop('disabled')) {
                        return;
                    }
                    self.handlePrintQuote();
                });

            // Removed conflicting listener for #ktvhvn-choose-voucher-btn
            // Logic is now handled by accounting-campaigns.js which triggers 'ktvhvnAccountingCampaignSelected'

            // Listen for campaign selection from accounting-campaigns.js
            $(document).on('ktvhvnAccountingCampaignSelected', function (e, data) {
                if (data) {
                    self.state.discount = parseFloat(data.discountAmount) || 0;
                    self.state.discountName = data.name || '';
                    self.state.selectedCampaignId = data.id || null;

                    // Re-render price display
                    self.displayPrice({
                        subtotal: parseFloat(self.$subtotalAmount.text().replace(/[^\d]/g, '')) || 0
                    });
                }
            });
        }

        /**
         * Show empty state
         */
        showEmptyState() {
            this.$emptyState.show();
            this.$selectedService.hide();
            this.$addonSection.hide();
            this.$paymentSection.hide();
            this.$customerSection.hide();
            // Luôn hiển thị khối tổng tiền để tránh bị ẩn bởi state trống
            this.$priceSummary.show();
        }

        /**
         * Hide empty state
         */
        hideEmptyState() {
            this.$emptyState.hide();
        }

        /**
         * Handle service selection
         */
        onServiceSelect(serviceId) {
            const $option = this.$serviceSelect.find('option:selected');
            const serviceName = $option.data('name');
            const servicePrice = parseFloat($option.data('price')) || 0;
            const serviceUnit = $option.data('unit') || 'month';

            this.state.service = {
                id: serviceId,
                name: serviceName,
                price: servicePrice,
                unit: serviceUnit
            };

            // Rebuild industry dropdown based on enabled_industries + per-service prices
            this.rebuildIndustryDropdown();

            // Rebuild revenue dropdown based on custom_revenue_tiers
            this.rebuildRevenueDropdown();

            // Create service config card
            this.createServiceCard();

            // Sync current form values into state
            this.state.months = parseInt($('#months-needed').val(), 10) || 3;
            this.state.invoices = parseInt($('#invoices-quarter').val(), 10) || 0;
            this.state.revenue_bracket = $('#revenue-bracket').val() || 'under_2';
            this.state.products = parseInt($('#product-count').val(), 10) || 0;
            this.state.industry = $('#industry-select').val() || '';

            // Show sections
            this.hideEmptyState();
            this.$selectedService.fadeIn(300);
            this.$addonSection.fadeIn(300);
            this.$paymentSection.fadeIn(300);
            this.$customerSection.fadeIn(300);

            // Update addons visibility (which also syncs state.addons), then calculate price
            this.updateConditionalAddons();
            this.calculatePrice();
        }

        /**
         * Create service configuration card
         */
        createServiceCard() {
            // Use current form layout; just mirror selected service info
            this.$selectedService.html(`
                <div class="ktvhvn-selected-service">
                    <div class="ktvhvn-selected-service__head">
                        <h3 id="service-title">${this.state.service.name}</h3>
                        <button type="button" class="button" id="remove-service-btn">
                            <span class="dashicons dashicons-no-alt"></span> ${window.ktvhvnAccounting?.i18n?.remove_service || 'Đổi dịch vụ'}
                        </button>
                    </div>
                    <p class="description">${window.ktvhvnAccounting?.i18n?.service_hint || ''}</p>
                </div>
            `);
        }

        /**
         * Remove service
         */
        removeService() {
            this.state.service = null;
            this.state.industry = '';
            this.state.addons = [];

            // Uncheck all addons
            $('.addon-checkbox-card input[type="checkbox"]').prop('checked', false);

            // Show empty state
            this.showEmptyState();

            // Clear selected service
            this.$selectedService.empty();
        }

        /**
         * Update industry dropdown to show per-service prices
         * This method updates the dropdown option text dynamically based on
         * the selected service's pricing_logic.industry_min_prices
         */
        updateIndustryDropdownPrices() {
            this.rebuildIndustryDropdown();
        }

        /**
         * Filter industry dropdown based on enabled_industries per service
         * Uses pricing_logic.enabled_industries (null = all, array = specific IDs)
         */
        filterIndustryDropdown() {
            this.rebuildIndustryDropdown();
        }

        /**
         * Rebuild industry dropdown based on enabled_industries and per-service prices
         * Removes disabled options so Select2 doesn't keep showing them.
         */
        rebuildIndustryDropdown() {
            const $industrySelect = $('#industry-select');
            const service = this.state.service;

            if (!service || !$industrySelect.length) {
                return;
            }

            const serviceConfig = this.config.services?.find(s => s.id === service.id);
            const pricingLogic = serviceConfig?.pricing_logic || {};
            const enabledIndustries = pricingLogic.enabled_industries;
            const perServicePrices = pricingLogic.industry_min_prices || {};
            const industries = Array.isArray(this.config.industries) ? this.config.industries : [];

            console.log('[rebuildIndustryDropdown] enabled_industries:', enabledIndustries);

            const placeholder = $industrySelect.find('option').first().clone();
            const currentVal = $industrySelect.val();

            let filteredIndustries = industries;
            if (Array.isArray(enabledIndustries)) {
                filteredIndustries = industries.filter(industry => enabledIndustries.includes(industry.id));
            }

            $industrySelect.empty().append(placeholder);

            filteredIndustries.forEach(industry => {
                let minPrice = 0;
                if (perServicePrices[industry.id] !== undefined) {
                    minPrice = parseFloat(perServicePrices[industry.id]) || 0;
                } else {
                    minPrice = parseFloat(industry.min_price) || 500000;
                }

                const priceText = new Intl.NumberFormat('vi-VN').format(minPrice) + 'đ/tháng';
                const $option = $('<option></option>');
                $option.val(industry.id);
                $option.text(`${industry.name} (từ ${priceText})`);
                $option.attr('data-min-price', minPrice);
                $industrySelect.append($option);
            });

            const stillValid = currentVal && filteredIndustries.some(industry => industry.id === currentVal);
            if (stillValid) {
                $industrySelect.val(currentVal);
                this.state.industry = currentVal;
            } else {
                $industrySelect.val('');
                this.state.industry = '';
            }

            if ($industrySelect.hasClass('select2-hidden-accessible')) {
                $industrySelect.trigger('change.select2');
            }
        }

        /**
         * Rebuild revenue dropdown based on custom_revenue_tiers
         * Uses pricing_logic.custom_revenue_tiers (null = use global, array = custom tiers)
         */
        rebuildRevenueDropdown() {
            const $revenueSelect = $('#revenue-bracket');
            const service = this.state.service;

            if (!$revenueSelect.length || !service) {
                return;
            }

            const serviceConfig = this.config.services?.find(s => s.id === service.id);
            const pricingLogic = serviceConfig?.pricing_logic || {};
            const customTiersRaw = pricingLogic.custom_revenue_tiers;
            const custom_revenue_tiers = Array.isArray(customTiersRaw)
                ? customTiersRaw
                : (customTiersRaw && typeof customTiersRaw === 'object' ? Object.values(customTiersRaw) : null);

            console.log('[rebuildRevenueDropdown] custom_revenue_tiers:', custom_revenue_tiers);

            // If no custom tiers, keep the global dropdown
            if (!custom_revenue_tiers || !Array.isArray(custom_revenue_tiers) || custom_revenue_tiers.length === 0) {
                return;
            }

            // Store current selected value
            const currentVal = $revenueSelect.val();

            // Clear existing options (except placeholder if any)
            $revenueSelect.find('option:not([value=""])').remove();

            // Build new options from custom tiers
            custom_revenue_tiers.forEach((tier, index) => {
                const id = 'custom_' + index;
                const label = tier.label || this.generateRevenueTierLabel(tier);
                const addon = parseFloat(tier.addon) || 0;
                const formattedAddon = new Intl.NumberFormat('vi-VN').format(addon);

                const $option = $(`<option value="${id}">${label} (+${formattedAddon}đ/tháng)</option>`);
                $option.attr('data-addon', addon);
                $revenueSelect.append($option);
            });

            // Restore selection if still valid, otherwise select first
            if ($revenueSelect.find(`option[value="${currentVal}"]`).length) {
                $revenueSelect.val(currentVal);
            } else if (custom_revenue_tiers.length > 0) {
                $revenueSelect.val('custom_0');
            }

            // Update state
            this.state.revenue_bracket = $revenueSelect.val();
        }

        /**
         * Generate label for a custom revenue tier
         */
        generateRevenueTierLabel(tier) {
            const from = parseFloat(tier.from) || 0;
            const to = tier.to !== null && tier.to !== undefined ? parseFloat(tier.to) : null;

            if (from === 0 && to !== null) {
                return `Dưới ${to} tỷ`;
            }
            if (to === null) {
                return `Trên ${from} tỷ`;
            }
            return `Từ ${from} - ${to} tỷ`;
        }

        /**
         * Update addon visibility based on conditional logic
         */
        updateConditionalAddons() {
            const self = this;

            if (!this.state.service) {
                return;
            }

            let visibleCount = 0;

            $('.addon-checkbox-card').each(function () {
                const $item = $(this);
                const showIf = $item.data('show-if');

                if (!showIf) {
                    return;
                }

                // Evaluate condition
                const shouldShow = self.conditionalEngine.evaluate(showIf, {
                    service: self.state.service.id,
                    industry: self.state.industry
                });

                if (shouldShow) {
                    $item.fadeIn(200);
                    visibleCount++;
                } else {
                    $item.fadeOut(200);
                    // Uncheck if hidden
                    $item.find('input[type="checkbox"]').prop('checked', false);
                }
            });

            // Show/hide addon section based on visible addons
            if (visibleCount > 0) {
                this.$addonSection.fadeIn(300);
            } else {
                this.$addonSection.fadeOut(300);
            }

            // FIX: Sync state.addons after visibility changes (hidden addons get unchecked)
            this.syncAddonsFromDOM();
        }

        /**
         * Sync state.addons with current DOM checkbox states
         * This ensures state.addons always reflects checked checkboxes
         */
        syncAddonsFromDOM() {
            this.state.addons = [];

            console.log('[syncAddonsFromDOM] Checking addon checkboxes...');
            const $checkedAddons = $('.addon-checkbox-card input[type="checkbox"]:checked');
            console.log('[syncAddonsFromDOM] Found checked addons:', $checkedAddons.length);

            $checkedAddons.each((i, el) => {
                const $checkbox = $(el);
                const addonData = {
                    id: $checkbox.val(),
                    name: $checkbox.data('name'),
                    price: parseFloat($checkbox.data('price')) || 0,
                    unit: $checkbox.data('unit') || 'time'
                };
                console.log('[syncAddonsFromDOM] Addon ' + i + ':', addonData);
                this.state.addons.push(addonData);
            });

            console.log('[syncAddonsFromDOM] Final state.addons:', JSON.stringify(this.state.addons));
        }

        /**
         * Handle addon checkbox change
         */
        onAddonChange() {
            this.syncAddonsFromDOM();
            this.calculatePrice();
        }

        /**
         * Calculate and display price
         */
        calculatePrice() {
            // Only calculate if service is selected
            if (!this.state.service) {
                return;
            }

            // Calculate price - TRUST pricing engine 100%
            let result = {};

            try {
                // DEBUG: Log input to pricing engine
                const pricingInput = {
                    service_id: this.state.service.id,
                    service_price: this.state.service.price,
                    service_unit: this.state.service.unit,
                    months: this.state.months,
                    invoices: this.state.invoices,
                    revenue_bracket: this.state.revenue_bracket || 'under_2',
                    addons: this.state.addons,
                    products: this.state.products,
                    industry_id: this.state.industry
                };
                console.log('[AccountingForm] Pricing input:', pricingInput);
                console.log('[AccountingForm] state.industry value:', JSON.stringify(this.state.industry));
                console.log('[AccountingForm] DOM #industry-select value:', $('#industry-select').val());

                result = this.pricingEngine.calculate(pricingInput);
                console.log('[AccountingForm] Pricing result from engine:', result);
            } catch (err) {
                console.error('[AccountingForm] Pricing engine EXCEPTION:', err);
                // Only fallback on exception, not on 0 result
                result = {
                    total: 0,
                    subtotal: 0,
                    base_price: 0,
                    revenue_addon: 0,
                    addons_total: 0,
                    invoice_surcharge: 0,
                    product_surcharge: 0
                };
            }

            // IMPORTANT: TRUST pricing engine result, even if it's 0
            // Do NOT override with fallback logic
            const safeResult = result;

            // Cache last computed pricing result for payment popup and PDF generation
            this.state.lastTotal = safeResult.total || 0;
            this.state.lastPricingResult = safeResult; // Store full result for PDF generation

            // Debug calc output to verify totals in production
            console.log('[AccountingForm] Final pricing result (trusted):', {
                input: { ...this.state },
                result: safeResult
            });

            // Show summary block when calculated
            this.$priceSummary.show();

            // Display results
            this.displayPrice(safeResult);

            // Load vouchers after price computed
            this.loadVouchers(safeResult.total || 0);
        }

        /**
         * Display price breakdown
         */
        displayPrice(result) {
            const formatPrice = (amount) => {
                return new Intl.NumberFormat('vi-VN', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(amount) + 'đ';
            };

            // DEBUG: Log displayPrice input
            console.log('[displayPrice] result:', result);
            console.log('[displayPrice] result.subtotal:', result.subtotal);
            console.log('[displayPrice] result.total:', result.total);
            console.log('[displayPrice] result.base_price:', result.base_price);
            console.log('[displayPrice] result.addons_total:', result.addons_total);

            // Đảm bảo khối tổng tiền luôn hiển thị
            this.$priceSummary.show();

            // Subtotal (service price without questionnaire)
            const subtotal = result.subtotal || result.total || 0;
            console.log('[displayPrice] calculated subtotal:', subtotal);
            this.$subtotalAmount.text(formatPrice(subtotal));

            // Voucher/Discount display logic
            if (this.state.discount > 0) {
                // Đã chọn voucher - ẩn nút chọn, hiện discount row
                this.$voucherRow.hide();
                this.$discountRow.css('display', 'flex');
                this.$discountAmount.text('-' + formatPrice(this.state.discount));
                this.$discountCampaignName.text(this.state.discountName ? '(' + this.state.discountName + ')' : '');
            } else {
                // Chưa chọn voucher - hiện nút chọn nếu có campaigns
                this.$discountRow.hide();
                const availableCampaigns = window.ktvhvnAvailableCampaigns || this.availableCampaigns || [];
                if (availableCampaigns.length > 0) {
                    this.$voucherRow.css('display', 'flex');
                } else {
                    this.$voucherRow.hide();
                }
            }

            // Total after discount
            // NOTE: Include questionnairePrice from add-on if available
            // In Accounting form, questionnaire price is displayed separately by accounting-integration.js
            // but needs to be included in the final total calculation here
            const questionnairePrice = parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
            const finalTotal = Math.max(0, subtotal + questionnairePrice - (this.state.discount || 0));
            this.$totalAmount.text(formatPrice(finalTotal));

            // BUG FIX: Update lastTotal to include questionnaire price and discount
            // This ensures payment popup shows the correct final amount
            // Previously lastTotal was set from pricing engine result (without questionnaire price)
            this.state.lastTotal = finalTotal;
            console.log('[displayPrice] Updated lastTotal for payment popup:', finalTotal);
        }

        /**
         * Handle form submission
         */
        handleSubmit(type) {
            const self = this;

            // Validate
            if (!this.validateForm()) {
                return;
            }

            // Check if auto-submit is disabled for payment button
            if (type === 'payment' && window.ktvhvnAccounting.auto_submit?.payment === false) {
                console.log('KTVHVN Accounting: Auto-submit disabled for payment, showing popup directly');
                this.showPaymentPopup(null, this.state.lastTotal);
                return;
            }

            // If total is zero but both service & industry selected, force a calc once more
            if (this.state.service && this.state.industry) {
                this.calculatePrice();
            }

            // Get customer data
            const customerData = {
                company_name: '',
                tax_code: '',
                contact_name: $('#contact-name').val().trim(),
                contact_phone: $('#contact-phone').val().trim(),
                contact_email: $('#contact-email').val().trim(),
                notes: $('#notes').val().trim()
            };

            // Get traffic source data
            const trafficData = this.getTrafficSourceForSubmission();

            // Prepare data (match server-side keys)
            const data = {
                action: 'ktvhvn_submit_accounting',
                ktvhvn_accounting_nonce: window.ktvhvnAccounting.nonce,
                service_id: this.state.service.id,
                industry_id: this.state.industry,
                months_needed: this.state.months,
                invoices_quarter: this.state.invoices,
                revenue_bracket: this.state.revenue_bracket || 'under_2',
                product_count: this.state.products,
                addons: this.state.addons.map(a => a.id),
                company_name: customerData.company_name,
                tax_code: customerData.tax_code,
                contact_name: customerData.contact_name,
                contact_phone: customerData.contact_phone,
                contact_email: customerData.contact_email,
                notes: customerData.notes,
                selected_campaign_id: this.state.selectedCampaignId || '',
                campaign_discount_amount: this.state.discount || 0,
                campaign_name: this.state.discountName || '',
                // Traffic source tracking
                traffic_source: trafficData.source || '',
                utm_source: trafficData.utm_source || '',
                utm_medium: trafficData.utm_medium || '',
                utm_campaign: trafficData.utm_campaign || '',
                referrer: trafficData.referrer || '',
                landing_page: trafficData.landing_page || '',
                first_touch_source: trafficData.first_touch_source || '',
                first_touch_date: trafficData.first_touch_date || ''
            };

            // Show loading
            let $btn;
            if (type === 'registration') {
                $btn = $('#submit-btn');
            } else if (type === 'payment') {
                $btn = $('#ktvhvn-accounting-pay-btn');
            } else if (type === 'print_quote') {
                $btn = $('#ktvhvn-print-quote-btn, #ktvhvn-accounting-print-btn').first();
            } else {
                $btn = $('#ktvhvn-accounting-pay-btn');
            }
            const originalText = $btn.html();
            $btn.prop('disabled', true).html('<i class="fa fa-spinner fa-spin"></i> Đang gửi...');

            // AJAX submit
            $.ajax({
                url: window.ktvhvnAccounting.ajaxUrl,
                type: 'POST',
                data: data,
                success: function (response) {
                    if (response.success) {
                        if (type === 'payment') {
                            if (response.data && response.data.submission_id) {
                                self.showPaymentPopup(response.data.submission_id, self.state.lastTotal);
                            }
                        } else if (type === 'print_quote') {
                            if (response.data && response.data.submission_id) {
                                self.generatePdf(response.data.submission_id);
                            }
                        } else if (type === 'registration') {
                            alert(response.data?.message || 'Gửi yêu cầu thành công! Chúng tôi sẽ liên hệ với bạn sớm nhất.');
                            self.resetForm();
                        }
                    } else {
                        alert('Có lỗi xảy ra: ' + (response.data?.message || 'Vui lòng thử lại'));
                    }
                },
                error: function () {
                    alert('Không thể kết nối đến server. Vui lòng thử lại sau.');
                },
                complete: function () {
                    $btn.prop('disabled', false).html(originalText);
                }
            });
        }

        /**
         * Load vouchers from ERPNext (accounting context)
         */
        loadVouchers(orderTotal) {
            const self = this;

            if (!orderTotal || orderTotal <= 0) {
                return;
            }

            $.post(window.ktvhvnAccounting.ajaxUrl, {
                action: 'ktvhvn_get_campaigns',
                nonce: window.ktvhvnAccounting.campaignNonce || (window.ktvhvnCourseReg ? window.ktvhvnCourseReg.nonce : ''),
                order_value: orderTotal,
                type: 'accounting'
            }).done(function (response) {
                if (response.success && response.data && response.data.campaigns) {
                    self.availableCampaigns = response.data.campaigns;
                    // Hiển thị nút chọn voucher nếu có campaigns và chưa chọn voucher
                    if (self.availableCampaigns.length > 0 && !self.state.discount) {
                        self.$voucherRow.css('display', 'flex');
                    }
                }
            }).fail(function () {
                // Silent fail
            });
        }

        /**
         * Choose voucher - show popup giống course form
         */
        /**
         * Choose voucher - DEPRECATED
         * Logic moved to accounting-campaigns.js
         */
        chooseVoucher() {
            // This method is deprecated and replaced by accounting-campaigns.js
            console.log('chooseVoucher in AccountingForm is deprecated. Using accounting-campaigns.js instead.');
        }

        /**
         * Get traffic source for form submission
         */
        getTrafficSourceForSubmission() {
            // Try to get traffic tracking data from localStorage
            let trackingData = { first_touch: null, last_touch: null };
            try {
                const stored = localStorage.getItem('ktvhvn_traffic_source');
                if (stored) {
                    trackingData = JSON.parse(stored);
                }
            } catch (e) {
                console.error('Cannot load traffic tracking:', e);
            }

            const data = trackingData.last_touch || trackingData.first_touch;

            if (!data) {
                return {
                    source: 'Direct / None',
                    referrer: '',
                    utm_source: '',
                    utm_medium: '',
                    utm_campaign: ''
                };
            }

            return {
                source: data.source_name || 'Unknown',
                referrer: data.referrer || '',
                landing_page: data.landing_page || '',
                utm_source: data.utm_source || '',
                utm_medium: data.utm_medium || '',
                utm_campaign: data.utm_campaign || '',
                utm_content: data.utm_content || '',
                utm_term: data.utm_term || '',
                first_touch_source: trackingData.first_touch ? trackingData.first_touch.source_name : '',
                first_touch_date: trackingData.first_touch ? new Date(trackingData.first_touch.timestamp).toISOString() : ''
            };
        }

        /**
         * Validate form
         */
        validateForm() {
            // Check service selected
            if (!this.state.service) {
                alert('Vui lòng chọn dịch vụ kế toán');
                return false;
            }

            // Check industry
            if (!this.state.industry) {
                alert('Vui lòng chọn ngành nghề');
                $('#industry-select').focus();
                return false;
            }

            // Check customer info
            const contactName = $('#contact-name').val().trim();
            const contactPhone = $('#contact-phone').val().trim();
            const contactEmail = $('#contact-email').val().trim();

            if (!contactName) {
                alert('Vui lòng nhập họ tên người liên hệ');
                $('#contact-name').focus();
                return false;
            }

            if (!contactPhone) {
                alert('Vui lòng nhập số điện thoại');
                $('#contact-phone').focus();
                return false;
            }

            if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
                alert('Email không hợp lệ');
                $('#contact-email').focus();
                return false;
            }

            return true;
        }

        /**
         * Handle print quote
         * Always uses the accounting direct PDF template to match the design
         */
        handlePrintQuote() {
            // Prevent duplicate PDF generation
            if (this.state.isPrintingPdf) {
                console.log('KTVHVN Accounting: PDF generation already in progress, ignoring');
                return;
            }

            if (!this.validateForm()) {
                return;
            }

            // Set flag to prevent duplicate calls
            this.state.isPrintingPdf = true;

            const $btn = $('#ktvhvn-print-quote-btn, #ktvhvn-accounting-print-btn').first();
            const originalText = $btn.html();
            $btn.prop('disabled', true).html('<i class="fa fa-spinner fa-spin"></i> Đang tạo PDF...');

            // Always use direct PDF generation for accounting form
            // This ensures the PDF matches the accounting service design
            console.log('KTVHVN Accounting: Generating accounting service PDF directly');
            this.generatePdfDirect($btn, originalText);
        }

        /**
         * Generate PDF directly without saving registration (accounting form)
         * Uses the new accounting PDF template with full form data
         */
        generatePdfDirect($btn, originalText) {
            const self = this;
            const nonce = window.ktvhvnCourseReg ? window.ktvhvnCourseReg.nonce : (window.ktvhvnAccounting ? window.ktvhvnAccounting.nonce : '');

            // Get pricing from last calculated result (already computed correctly by pricingEngine)
            const pricingResult = this.state.lastPricingResult || {};
            const questionnairePrice = parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
            const subtotal = (pricingResult.subtotal || pricingResult.total || 0) + questionnairePrice;

            // Build addons list
            const addonIds = this.state.addons.map(a => a.id);

            // Use correct values from pricing engine result
            const basePrice = pricingResult.base_price || 0;
            const addonsTotal = pricingResult.addons_total || 0;

            // Collect questionnaire data for PDF (Giá chính xác section)
            const questionnaireItems = this.collectQuestionnaireItemsForPdf();

            // Send full accounting form data for proper PDF generation
            $.post(window.ktvhvnAccounting.ajaxUrl, {
                action: 'ktvhvn_generate_pdf_quote',
                nonce: nonce,
                direct_mode: '1',
                pdf_type: 'accounting',
                // Customer info
                contact_name: $('#contact-name').val().trim(),
                contact_phone: $('#contact-phone').val().trim(),
                contact_email: $('#contact-email').val().trim(),
                notes: $('#notes').val().trim(),
                // Service info
                service_id: this.state.service ? this.state.service.id : '',
                industry_id: this.state.industry || '',
                months_needed: this.state.months || 0,
                invoices_quarter: this.state.invoices || 0,
                revenue_bracket: this.state.revenue_bracket || 'under_2',
                product_count: this.state.products || 0,
                addons: addonIds,
                // Pricing info - use values from pricing engine
                base_price: basePrice,
                subtotal: subtotal,
                addons_total: addonsTotal,
                discount: this.state.discount || 0,
                discount_name: this.state.discountName || '',
                total: Math.max(0, subtotal - (this.state.discount || 0)),
                // Questionnaire data for "Giá chính xác" section
                questionnaire_price: questionnairePrice,
                questionnaire_items: JSON.stringify(questionnaireItems)
            })
                .done(function (response) {
                    if (response.success && response.data && response.data.file_url) {
                        window.open(response.data.file_url, '_blank');
                    } else {
                        alert('Có lỗi khi tạo PDF: ' + (response.data?.message || 'Unknown error'));
                    }
                })
                .fail(function () {
                    alert('Không thể tạo PDF. Vui lòng thử lại.');
                })
                .always(function () {
                    $btn.prop('disabled', false).html(originalText);
                    self.state.isPrintingPdf = false; // Reset flag when done
                });
        }

        normalizeQuestionnaireText(value) {
            if (!value) {
                return '';
            }
            return String(value).replace(/\s+/g, ' ').trim();
        }

        getQuestionnaireQuestionLabel($question) {
            const $label = $question.children('label').first();
            if ($label.length) {
                const $clone = $label.clone();
                $clone.find('.questionnaire-required').remove();
                const text = this.normalizeQuestionnaireText($clone.text());
                if (text) {
                    return text;
                }
            }

            const fallback = this.normalizeQuestionnaireText($question.attr('data-field-name'));
            return fallback || 'Giá chính xác';
        }

        getQuestionnaireOptionLabel($input) {
            const $label = $input.closest('label');
            if ($label.length) {
                const $clone = $label.clone();
                $clone.find('.questionnaire-option-price, small').remove();
                const text = this.normalizeQuestionnaireText($clone.text());
                if (text) {
                    return text;
                }
            }

            return this.normalizeQuestionnaireText($input.val());
        }

        /**
         * Collect questionnaire items with prices for PDF generation
         * This reads the selected questionnaire options from the DOM
         * @returns {Array} Array of {label, price} objects
         */
        collectQuestionnaireItemsForPdf() {
            const items = [];
            const months = this.state.months || 1;
            const $container = $('#ktvhvn-accounting-questionnaire-container');
            let hasContactRequired = false;
            const hasContactKeyword = (rawValue) => {
                const value = String(rawValue || '').toLowerCase();
                return value.includes('contact') || value.includes('lien_he') || value.includes('liên hệ');
            };

            if (!$container.length) {
                return items;
            }

            $container.find('.questionnaire-question[data-visibility="visible"]').each((index, element) => {
                const $question = $(element);
                const questionLabel = this.getQuestionnaireQuestionLabel($question);

                $question.find('.questionnaire-checkbox:checked').each((checkboxIndex, checkboxElement) => {
                    const $checkbox = $(checkboxElement);
                    const checkboxPrice = parseFloat($checkbox.attr('data-price')) || 0;
                    if (hasContactKeyword($checkbox.val())) {
                        hasContactRequired = true;
                    }
                    if (checkboxPrice <= 0) {
                        return;
                    }

                    items.push({
                        label: `${questionLabel}: ${this.getQuestionnaireOptionLabel($checkbox)}`,
                        price: checkboxPrice * months
                    });
                });

                const $radio = $question.find('.questionnaire-radio:checked');
                if ($radio.length) {
                    const radioPrice = parseFloat($radio.attr('data-price')) || 0;
                    if (hasContactKeyword($radio.val())) {
                        hasContactRequired = true;
                    }
                    if (radioPrice > 0) {
                        items.push({
                            label: `${questionLabel}: ${this.getQuestionnaireOptionLabel($radio)}`,
                            price: radioPrice * months
                        });
                    }
                }

                const $select = $question.find('.questionnaire-select');
                if ($select.length) {
                    const $selectedOption = $select.find('option:selected');
                    const selectedPrice = parseFloat($selectedOption.attr('data-price')) || 0;
                    if (hasContactKeyword($selectedOption.val())) {
                        hasContactRequired = true;
                    }
                    if (selectedPrice > 0) {
                        const selectedLabel = this.normalizeQuestionnaireText($selectedOption.text());
                        items.push({
                            label: `${questionLabel}: ${selectedLabel}`,
                            price: selectedPrice * months
                        });
                    }
                }
            });

            return hasContactRequired ? [] : items;
        }

        /**
         * Show Payment Popup (VietQR)
         * 
         * NOTE: totalAmount is lastTotal from displayPrice() which ALREADY includes:
         * - Service base price
         * - Questionnaire addons
         * - Discount (already subtracted)
         * 
         * DO NOT apply discount again here!
         */
        showPaymentPopup(submissionId, totalAmount) {
            const $popup = $('#ktvhvn-payment-popup');
            const $amount = $('#ktvhvn-payment-amount');
            const $code = $('#ktvhvn-payment-code-text');
            const $qr = $('#ktvhvn-qr-code-image');
            const $discountNotice = $('#ktvhvn-payment-discount-notice');

            // BUG FIX: totalAmount already has discount applied in displayPrice()
            // DO NOT subtract discount again - that was causing double discount!
            const finalTotal = Math.max(0, totalAmount || 0);
            const discount = this.state.discount || 0;

            console.log('[showPaymentPopup] totalAmount (already with discount):', totalAmount);
            console.log('[showPaymentPopup] finalTotal for popup:', finalTotal);

            $amount.text(new Intl.NumberFormat('vi-VN', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(finalTotal) + ' VND');

            // Payment code: KTVHVN + last 6 digits of phone
            const phone = $('#contact-phone').val().trim().replace(/[\s\-\.]/g, '');
            const code = 'KTVHVN' + phone.slice(-6);
            $code.text(code);

            // Build VietQR URL
            const qrUrl = `https://img.vietqr.io/image/BIDV-1261274211-compact2.png?amount=${Math.round(finalTotal)}&addInfo=${encodeURIComponent(code)}&accountName=CONG TY CP DAU TU VA CONG NGHE VIET HUNG`;
            $qr.attr('src', qrUrl);

            if (discount > 0) {
                $discountNotice.show().find('i').next().text(`Đã áp dụng ưu đãi: -${new Intl.NumberFormat('vi-VN', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(discount)}đ`);
            } else {
                $discountNotice.hide();
            }

            $popup.fadeIn(200);

            $popup.find('.ktvhvn-popup-close, .ktvhvn-popup-overlay').off('click').on('click', function () {
                $popup.fadeOut(200);
            });
        }

        /**
         * Generate PDF quote using submission id
         */
        generatePdf(submissionId) {
            const self = this;
            const nonce = window.ktvhvnCourseReg ? window.ktvhvnCourseReg.nonce : (window.ktvhvnAccounting ? window.ktvhvnAccounting.nonce : '');
            $.post(window.ktvhvnAccounting.ajaxUrl, {
                action: 'ktvhvn_generate_pdf_quote',
                nonce: nonce,
                registration_id: submissionId
            })
                .done(function (pdfResponse) {
                    if (pdfResponse.success && pdfResponse.data && pdfResponse.data.file_url) {
                        window.open(pdfResponse.data.file_url, '_blank');
                    } else {
                        alert('Có lỗi khi tạo PDF: ' + (pdfResponse.data?.message || 'Unknown error'));
                    }
                })
                .fail(function () {
                    alert('Không thể tạo PDF. Vui lòng thử lại.');
                })
                .always(function () {
                    self.state.isPrintingPdf = false; // Reset flag when done
                });
        }

        /**
         * Reset form fields after successful registration
         */
        resetForm() {
            this.removeService();
            $('#contact-name, #contact-phone, #contact-email, #notes').val('');
            this.state.discount = 0;
            this.state.discountName = '';
            this.state.selectedCampaignId = null;
            this.displayPrice({ total: 0, subtotal: 0 });
        }
    };

    // Initialize form on document ready
    $(document).ready(function () {
        // Check for accounting form container (either #ktvhvn-accounting-form or #ktvhvn-service-picker)
        if (($('#ktvhvn-accounting-form').length || $('#ktvhvn-service-picker').length) && window.ktvhvnAccounting) {
            window.ktvhvnAccountingForm = new window.KTVHVN.AccountingForm();
        }
    });

})(jQuery);
